import { useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
const APP_URL = import.meta.env.VITE_APP_URL || 'http://localhost:5173';

type State = 'form' | 'submitting' | 'success' | 'error' | 'invalid';

export default function App() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const page = window.location.pathname;

  // Route: /reset-password?token=xxx
  if (page === '/reset-password' && token) {
    return <ResetPassword token={token} />;
  }

  // Fallback: redirect to main app
  return (
    <Shell>
      <div className="text-center space-y-4">
        <div className="text-5xl">🔗</div>
        <h1 className="text-xl font-bold text-slate-900">Nothing here</h1>
        <p className="text-sm text-slate-500">This page doesn't exist.</p>
        <a
          href={APP_URL}
          className="inline-block mt-4 px-6 py-3 bg-[#fa8585] text-white font-bold rounded-xl hover:bg-[#fa8585]/90 transition-colors"
        >
          Go to Nudge
        </a>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#f8f5f5] flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <svg viewBox="0 0 64 64" className="size-10">
            <rect width="64" height="64" rx="16" fill="white" />
            <path
              d="M32 52C32 52 12 40 12 26C12 20 17 14 23 14C27 14 30 16 32 19C34 16 37 14 41 14C47 14 52 20 52 26C52 40 32 52 32 52Z"
              fill="#fa8585"
            />
          </svg>
          <span className="text-2xl font-bold text-slate-900">Nudge</span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          {children}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          &copy; {new Date().getFullYear()} Nudge. Stay connected.
        </p>
      </div>
    </div>
  );
}

function ResetPassword({ token }: { token: string }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState<State>('form');
  const [error, setError] = useState('');

  const validate = (): string | null => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain a lowercase letter';
    if (!/\d/.test(password)) return 'Password must contain a number';
    if (password !== confirm) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setState('submitting');
    setError('');

    try {
      const res = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (res.status === 400 && data?.code === 'INVALID_TOKEN') {
          setState('invalid');
          return;
        }
        throw new Error(data?.message || 'Something went wrong');
      }

      setState('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setState('form');
    }
  };

  if (state === 'success') {
    return (
      <Shell>
        <div className="text-center space-y-4">
          <div className="size-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto">
            <svg className="size-7 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-slate-900">Password Updated</h1>
          <p className="text-sm text-slate-500">
            Your password has been changed successfully. You can now log in with your new password.
          </p>
          <a
            href={APP_URL}
            className="inline-block mt-4 px-6 py-3 bg-[#fa8585] text-white font-bold rounded-xl hover:bg-[#fa8585]/90 transition-colors"
          >
            Open Nudge
          </a>
        </div>
      </Shell>
    );
  }

  if (state === 'invalid') {
    return (
      <Shell>
        <div className="text-center space-y-4">
          <div className="size-14 bg-red-50 rounded-full flex items-center justify-center mx-auto">
            <svg className="size-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-slate-900">Link Expired</h1>
          <p className="text-sm text-slate-500">
            This reset link has expired or has already been used. Please request a new one from the app.
          </p>
          <a
            href={APP_URL}
            className="inline-block mt-4 px-6 py-3 bg-[#fa8585] text-white font-bold rounded-xl hover:bg-[#fa8585]/90 transition-colors"
          >
            Open Nudge
          </a>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-5">
        <div className="text-center">
          <h1 className="text-xl font-bold text-slate-900">Reset Password</h1>
          <p className="text-sm text-slate-500 mt-1">Choose a new password for your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              New Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-slate-900 font-medium focus:ring-2 focus:ring-[#fa8585] focus:border-[#fa8585] outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat your password"
              required
              className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-slate-900 font-medium focus:ring-2 focus:ring-[#fa8585] focus:border-[#fa8585] outline-none transition-all"
            />
          </div>

          {error && (
            <p className="text-sm text-red-500 font-medium bg-red-50 p-3 rounded-xl">{error}</p>
          )}

          <button
            type="submit"
            disabled={state === 'submitting'}
            className="w-full py-3.5 bg-[#fa8585] text-white font-bold rounded-xl hover:bg-[#fa8585]/90 disabled:opacity-50 transition-all"
          >
            {state === 'submitting' ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        <p className="text-center text-[11px] text-slate-400">
          Must contain uppercase, lowercase, and a number
        </p>
      </div>
    </Shell>
  );
}
